/**
 * Single source for the project cost estimator: profiles, TR/EN synonyms, TRY bands, the
 * week→month rounding policy and the range formatting. Plain JS so both the SPA
 * (src/lib/projectCostEstimate.ts) and Node build data (src/data/tools.data.mjs) read the same values.
 *
 * TRY bands (KDV hariç), premium butik ajans konumlandırması — alt segment outsourcing değil.
 * Piyasa kalibrasyonu: 2026-10-25 — TR senior/blended ~2.000–3.000 TL/saat ve günlük katsayılar;
 * AB/US butik SaaS MVP ~$60k–$120k; TR kaliteli ajanslar ~$50–$99/saat bandı.
 * Kaynak özet: docs/audit/2026-10-23-cursor-package/evidence/outputs/project-cost-market-adjustment-2026-10-25.md
 */

/** @typedef {'marketing_site' | 'saas_mvp' | 'mobile_product' | 'ecommerce' | 'general'} ProjectCostProfile */
/** @typedef {'tr' | 'en'} ProjectCostLang */
/** @typedef {{ min: number; max: number; weeksMin: number; weeksMax: number }} ProjectCostBand */

/** @type {Record<ProjectCostProfile, { mvp: ProjectCostBand; platform: ProjectCostBand; design: ProjectCostBand; maintenance: ProjectCostBand }>} */
export const PROJECT_COST_BANDS = {
  marketing_site: {
    mvp: { min: 95_000, max: 195_000, weeksMin: 2, weeksMax: 5 },
    platform: { min: 165_000, max: 320_000, weeksMin: 4, weeksMax: 9 },
    design: { min: 40_000, max: 90_000, weeksMin: 1, weeksMax: 3 },
    maintenance: { min: 10_000, max: 22_000, weeksMin: 0, weeksMax: 0 },
  },
  saas_mvp: {
    mvp: { min: 550_000, max: 1_050_000, weeksMin: 8, weeksMax: 14 },
    platform: { min: 1_200_000, max: 2_200_000, weeksMin: 16, weeksMax: 28 },
    design: { min: 85_000, max: 165_000, weeksMin: 3, weeksMax: 6 },
    maintenance: { min: 35_000, max: 65_000, weeksMin: 0, weeksMax: 0 },
  },
  mobile_product: {
    mvp: { min: 420_000, max: 820_000, weeksMin: 1, weeksMax: 8 },
    platform: { min: 980_000, max: 1_850_000, weeksMin: 14, weeksMax: 22 },
    design: { min: 75_000, max: 145_000, weeksMin: 2, weeksMax: 5 },
    maintenance: { min: 28_000, max: 52_000, weeksMin: 0, weeksMax: 0 },
  },
  ecommerce: {
    mvp: { min: 380_000, max: 720_000, weeksMin: 7, weeksMax: 11 },
    platform: { min: 880_000, max: 1_650_000, weeksMin: 12, weeksMax: 20 },
    design: { min: 70_000, max: 130_000, weeksMin: 2, weeksMax: 5 },
    maintenance: { min: 25_000, max: 48_000, weeksMin: 0, weeksMax: 0 },
  },
  general: {
    mvp: { min: 350_000, max: 680_000, weeksMin: 6, weeksMax: 10 },
    platform: { min: 850_000, max: 1_600_000, weeksMin: 12, weeksMax: 22 },
    design: { min: 70_000, max: 140_000, weeksMin: 2, weeksMax: 5 },
    maintenance: { min: 22_000, max: 42_000, weeksMin: 0, weeksMax: 0 },
  },
};

/** Explicit-choice order for the tool's project type select (index 0 = infer from text). */
export const PROJECT_COST_PROFILE_ORDER = ['marketing_site', 'saas_mvp', 'mobile_product', 'ecommerce', 'general'];

/** @type {Record<ProjectCostProfile, { tr: string; en: string }>} */
export const PROJECT_COST_PROFILE_LABEL = {
  marketing_site: { tr: 'Kurumsal / tanıtım sitesi', en: 'Corporate / marketing website' },
  saas_mvp: { tr: 'Abonelikli B2B SaaS / MVP', en: 'Subscription B2B SaaS / MVP' },
  mobile_product: { tr: 'Mobil ürün', en: 'Mobile product' },
  ecommerce: { tr: 'E-ticaret / marketplace', en: 'E-commerce / marketplace' },
  general: { tr: 'Genel / özel yazılım (ERP, CRM, panel)', en: 'General / custom software (ERP, CRM, internal tools)' },
};

export const PROJECT_COST_SCOPE_LABELS = {
  tr: ['MVP / çekirdek teslim', 'Platform / genişletilmiş kapsam', 'UI/UX', 'Bakım'],
  en: ['MVP / core delivery', 'Platform / expanded scope', 'UI/UX', 'Maintenance'],
};

/**
 * Strong signals name the product type. Weak signals ("web sitesi", "MVP", "uygulama") only decide
 * when nothing strong matched, so "e-ticaret web sitesi" stays e-commerce and "mobil MVP" stays mobile.
 * Patterns run on normalizeProjectCostText() output (lowercase, Turkish letters folded to ASCII).
 */
const STRONG_SIGNALS = {
  marketing_site: [
    /\b(tek sayfa\w*|single[- ]page|one[- ]page|landing( page)?|brochure|vitrin|tanitim)\b/,
    /\b(kurumsal|sirket|firma|corporate|company|business)\s+(web\s*|internet\s*)?(site|sitesi|siteniz|website)\b/,
    /\bmarketing\s+(web\s*)?site\b|\bmarketing website\b/,
  ],
  saas_mvp: [/\b(saas|abonelik\w*|subscription|multi[- ]?tenant|tenant)\b/],
  mobile_product: [/\b(mobil|mobile|ios|android|flutter|react native|app store|google play)\b/],
  ecommerce: [
    /\b(e-?ticaret|e ticaret|e-?commerce|e commerce|online (magaza|store|shop)|pazaryeri|marketplace|shopify|woocommerce)\b/,
  ],
  general: [
    /\b(erp|crm|mrp|wms|ik sistemi|hr system|internal tool|ic arac\w*|admin panel\w*|yonetim panel\w*|dashboard|portal|otomasyon\w*|automation|ozel yazilim|custom software|entegrasyon\w*|integration)\b/,
  ],
};

const WEAK_SIGNALS = {
  marketing_site: [/\b(web\s*sitesi|internet\s*sitesi|website|web site)\b/],
  saas_mvp: [/\b(mvp|b2b)\b/],
  general: [/\b(uygulama|app|yazilim|software)\b/],
};

/** Tie-break among several strong matches: the broader scope carries the estimate. */
const MIXED_PRIORITY = ['saas_mvp', 'ecommerce', 'mobile_product', 'general', 'marketing_site'];

/** @param {string} input */
export function normalizeProjectCostText(input) {
  return String(input || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {string} input
 * @returns {{ profile: ProjectCostProfile; matched: ProjectCostProfile[]; confidence: 'explicit' | 'mixed' | 'weak' | 'unknown' }}
 */
export function resolveProjectCostProfile(input) {
  const text = normalizeProjectCostText(input);
  const strong = MIXED_PRIORITY.filter((p) => (STRONG_SIGNALS[p] || []).some((re) => re.test(text)));
  if (strong.length === 1) return { profile: strong[0], matched: strong, confidence: 'explicit' };
  if (strong.length > 1) return { profile: strong[0], matched: strong, confidence: 'mixed' };
  const weak = MIXED_PRIORITY.filter((p) => (WEAK_SIGNALS[p] || []).some((re) => re.test(text)));
  if (weak.length) return { profile: weak[0], matched: weak, confidence: 'weak' };
  return { profile: 'general', matched: [], confidence: 'unknown' };
}

/** @param {number} amount @param {ProjectCostLang} lang */
export function formatProjectCostTry(amount, lang) {
  return `₺${amount.toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')}`;
}

/** @param {number} min @param {number} max @param {ProjectCostLang} lang */
export function formatProjectCostRange(min, max, lang) {
  return `${formatProjectCostTry(min, lang)}–${formatProjectCostTry(max, lang)}`;
}

/**
 * Rounding policy: up to 8 weeks is shown in weeks; longer bands in whole months
 * (4 weeks = 1 month, half rounds up: 14 weeks → 4 months). Maintenance bands are monthly.
 * @param {number} weeksMin @param {number} weeksMax @param {ProjectCostLang} lang
 */
export function formatProjectCostDuration(weeksMin, weeksMax, lang) {
  if (weeksMin <= 0 && weeksMax <= 0) return lang === 'tr' ? 'aylık' : 'monthly';
  if (weeksMax <= 8) return lang === 'tr' ? `${weeksMin}–${weeksMax} hafta` : `${weeksMin}–${weeksMax} weeks`;
  const monthsMin = Math.max(1, Math.round(weeksMin / 4));
  const monthsMax = Math.max(monthsMin, Math.round(weeksMax / 4));
  return lang === 'tr' ? `${monthsMin}–${monthsMax} ay` : `${monthsMin}–${monthsMax} months`;
}

/**
 * "MVP / core delivery: ₺550,000–₺1,050,000 (2–4 months)" — used by the tool example so the static
 * copy cannot drift from the live result.
 * @param {ProjectCostProfile} profile @param {'mvp' | 'platform' | 'design' | 'maintenance'} key @param {ProjectCostLang} lang
 */
export function formatProjectCostBandLine(profile, key, lang) {
  const band = PROJECT_COST_BANDS[profile][key];
  const index = ['mvp', 'platform', 'design', 'maintenance'].indexOf(key);
  return `${PROJECT_COST_SCOPE_LABELS[lang][index]}: ${formatProjectCostRange(band.min, band.max, lang)} (${formatProjectCostDuration(band.weeksMin, band.weeksMax, lang)})`;
}
